import { forwardRef, type ReactNode } from 'react';
import { Badge as ChakraBadge, type BadgeProps as ChakraBadgeProps } from '@chakra-ui/react';

/**
 * Badge primitive + config-driven StatusBadge.
 *
 * `StatusBadge` maps a small set of semantic tones to consistent colors so
 * status pills look the same everywhere. Drive it from a per-domain config
 * object (status -> { tone, label }) rather than hand-coloring badges.
 */
export interface BadgeProps extends ChakraBadgeProps {}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>((props, ref) => (
	<ChakraBadge ref={ref} {...props} />
));
Badge.displayName = 'Badge';

export type StatusTone = 'neutral' | 'brand' | 'accent' | 'success' | 'warning' | 'error' | 'info';

const TONE_TO_SCHEME: Record<StatusTone, string> = {
	neutral: 'gray',
	brand: 'brand',
	accent: 'accent',
	success: 'green',
	warning: 'orange',
	error: 'red',
	info: 'brand',
};

export interface StatusBadgeProps extends Omit<ChakraBadgeProps, 'colorScheme'> {
	tone?: StatusTone;
	children: ReactNode;
}

export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(
	({ tone = 'neutral', variant = 'subtle', children, ...rest }, ref) => (
		<ChakraBadge
			ref={ref}
			colorScheme={TONE_TO_SCHEME[tone]}
			variant={variant}
			px={2}
			py={0.5}
			{...rest}
		>
			{children}
		</ChakraBadge>
	),
);
StatusBadge.displayName = 'StatusBadge';

export default Badge;
